import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { 
  TreeDeciduous, 
  Plus,
  Upload,
  DollarSign,
  Loader2,
  Settings,
  BarChart3,
  FileJson,
  Users,
  TrendingUp,
  Flame,
  TreePine,
  Sprout,
  Edit,
  Trash2,
  Save,
  X,
  Image,
  Eye,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Camera,
  Video,
  FileText,
  Check,
  XCircle,
  Target,
  Hammer,
  MapPin,
  Clock
} from 'lucide-react';

const API = `${import.meta.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const { t } = useLanguage();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  // Core state
  const [stats, setStats] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Donation categories state
  const [categories, setCategories] = useState({});
  const [editingCategory, setEditingCategory] = useState(null);
  
  // Equipment state
  const [equipment, setEquipment] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('depining');
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [newEquipment, setNewEquipment] = useState({
    name: '', brand: '', cost: 0, quantity: 1, priority: 'medium', status: 'needed'
  });

  // Form states
  const [newTree, setNewTree] = useState({ lat: -42.25, lng: -71.50, species: 'lenga' });
  const [newBatch, setNewBatch] = useState({ name: '', description: '', target_count: 100 });
  const [newExpense, setNewExpense] = useState({ category: 'seedlings', amount: 0, description: '' });
  const [importJson, setImportJson] = useState('');
  
  // Mission verification state
  const [pendingProofs, setPendingProofs] = useState([]);
  const [loadingMissions, setLoadingMissions] = useState(false);
  const [verifyingProof, setVerifyingProof] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Community submissions state
  const [communitySubmissions, setCommunitySubmissions] = useState([]);
  const [submissionCounts, setSubmissionCounts] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [submissionFilter, setSubmissionFilter] = useState('pending');
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);

  // Bypass auth for demo
  const [bypassAuth, setBypassAuth] = useState(true);

  useEffect(() => {
    if (bypassAuth) {
      fetchAdminData();
      fetchCommunitySubmissions();
    } else if (!authLoading) {
      if (!isAuthenticated) {
        navigate('/');
        return;
      }
      if (user?.role !== 'admin') {
        navigate('/');
        return;
      }
      fetchAdminData();
      fetchCommunitySubmissions();
    }
  }, [isAuthenticated, authLoading, user, navigate, bypassAuth]);

  const fetchAdminData = async () => {
    try {
      // Fetch dashboard data from API
      const dashboardRes = await fetch(`${API}/admin/dashboard`);
      if (dashboardRes.ok) {
        const data = await dashboardRes.json();
        setStats(data.stats);
        setBatches(data.batches || []);
        
        // Convert categories array to object format for UI
        const catObj = {};
        (data.categories || []).forEach(cat => {
          catObj[cat.slug] = {
            id: cat.slug,
            icon: cat.icon,
            name: cat.name_en,
            goal: cat.goal,
            raised: cat.raised || 0,
            color: cat.color
          };
        });
        setCategories(catObj);
      }
      
      // Fetch equipment for all categories
      const categorySlugs = ['depining', 'firefighting', 'reforestation', 'research', 'biosphere'];
      const equipmentObj = {};
      for (const slug of categorySlugs) {
        try {
          const eqRes = await fetch(`${API}/categories/${slug}/equipment`);
          if (eqRes.ok) {
            const eqData = await eqRes.json();
            equipmentObj[slug] = (eqData.equipment || []).map(eq => ({
              id: eq.equipment_id,
              name: eq.name,
              brand: eq.brand,
              cost: eq.cost,
              quantity: eq.quantity,
              priority: eq.priority,
              status: eq.status
            }));
          }
        } catch (e) {
          console.error(`Failed to fetch equipment for ${slug}:`, e);
        }
      }
      setEquipment(equipmentObj);
      
    } catch (e) {
      console.error('Failed to fetch admin data:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchCommunitySubmissions = async (statusFilter = submissionFilter) => {
    setLoadingSubmissions(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      const response = await fetch(`${API}/admin/community/submissions?${params}`);
      if (response.ok) {
        const data = await response.json();
        setCommunitySubmissions(data.submissions || []);
        setSubmissionCounts(data.counts || { pending: 0, approved: 0, rejected: 0, total: 0 });
      }
    } catch (e) {
      console.error('Failed to fetch community submissions:', e);
    } finally {
      setLoadingSubmissions(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'community') {
      fetchCommunitySubmissions(submissionFilter);
    }
    if (activeTab === 'missions') {
      fetchPendingProofs();
    }
  }, [submissionFilter, activeTab]);

  // Mission verification functions
  const fetchPendingProofs = async () => {
    setLoadingMissions(true);
    try {
      const response = await fetch(`${API}/admin/missions/pending-proofs`);
      if (response.ok) {
        const data = await response.json();
        setPendingProofs(data.proofs || []);
      }
    } catch (e) {
      console.error('Failed to fetch pending proofs:', e);
      toast.error('Failed to load pending proofs');
    } finally {
      setLoadingMissions(false);
    }
  };

  const handleVerifyProof = async (submissionId, decision) => {
    setVerifyingProof(submissionId);
    try {
      const response = await fetch(`${API}/admin/missions/${submissionId}/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          decision,
          rejection_reason: decision === 'rejected' ? 'Proof does not meet requirements' : null
        })
      });
      
      if (response.ok) {
        toast.success(decision === 'approved' 
          ? 'Mission verified! Payment will be processed.' 
          : 'Proof rejected. Worker will be notified.');
        fetchPendingProofs();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to verify proof');
      }
    } catch (e) {
      console.error('Failed to verify proof:', e);
      toast.error(e.message || 'Failed to verify proof');
    } finally {
      setVerifyingProof(null);
    }
  };

  const handleReviewSubmission = async (submissionId, action) => {
    try {
      const response = await fetch(`${API}/admin/community/submissions/${submissionId}/review`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action })
      });
      
      if (response.ok) {
        toast.success(`Submission ${action === 'approve' ? 'approved' : 'rejected'}`);
        fetchCommunitySubmissions(submissionFilter);
      } else {
        throw new Error('Failed to review submission');
      }
    } catch (e) {
      toast.error('Failed to review submission');
    }
  };

  const handleDeleteSubmission = async (submissionId) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;
    
    try {
      const response = await fetch(`${API}/admin/community/submissions/${submissionId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        toast.success('Submission deleted');
        fetchCommunitySubmissions(submissionFilter);
      } else {
        throw new Error('Failed to delete submission');
      }
    } catch (e) {
      toast.error('Failed to delete submission');
    }
  };

  // Category management
  const handleUpdateCategory = (categoryId, field, value) => {
    setCategories(prev => ({
      ...prev,
      [categoryId]: {
        ...prev[categoryId],
        [field]: field === 'goal' || field === 'raised' ? parseFloat(value) || 0 : value
      }
    }));
  };

  const saveCategory = async (categoryId) => {
    // In production, save to backend
    toast.success(`Category "${categories[categoryId].name}" updated successfully`);
    setEditingCategory(null);
  };

  // Equipment management
  const handleAddEquipment = () => {
    if (!newEquipment.name || !newEquipment.brand) {
      toast.error('Please fill in name and brand');
      return;
    }
    
    const newId = Math.max(...equipment[selectedCategory].map(e => e.id), 0) + 1;
    setEquipment(prev => ({
      ...prev,
      [selectedCategory]: [...prev[selectedCategory], { ...newEquipment, id: newId }]
    }));
    setNewEquipment({ name: '', brand: '', cost: 0, quantity: 1, priority: 'medium', status: 'needed' });
    toast.success('Equipment added successfully');
  };

  const handleUpdateEquipment = (id, field, value) => {
    setEquipment(prev => ({
      ...prev,
      [selectedCategory]: prev[selectedCategory].map(item => 
        item.id === id ? { ...item, [field]: field === 'cost' || field === 'quantity' ? parseFloat(value) || 0 : value } : item
      )
    }));
  };

  const handleDeleteEquipment = (id) => {
    setEquipment(prev => ({
      ...prev,
      [selectedCategory]: prev[selectedCategory].filter(item => item.id !== id)
    }));
    toast.success('Equipment removed');
  };

  const handleCreateTree = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Mock create
      toast.success('Tree created successfully');
      setNewTree({ lat: -42.25, lng: -71.50, species: 'lenga' });
      fetchAdminData();
    } catch (e) {
      toast.error('Failed to create tree');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateBatch = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      toast.success('Batch created successfully');
      setNewBatch({ name: '', description: '', target_count: 100 });
      fetchAdminData();
    } catch (e) {
      toast.error('Failed to create batch');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateExpense = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      toast.success('Expense recorded');
      setNewExpense({ category: 'seedlings', amount: 0, description: '' });
      fetchAdminData();
    } catch (e) {
      toast.error('Failed to record expense');
    } finally {
      setSubmitting(false);
    }
  };

  const getTotalEquipmentCost = (categoryId) => {
    return equipment[categoryId]?.reduce((sum, item) => sum + (item.cost * item.quantity), 0) || 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background" data-testid="admin-page">
        <Navbar />
        <main className="pt-24 pb-16 px-4">
          <div className="max-w-7xl mx-auto text-center">
            <Loader2 className="w-12 h-12 mx-auto animate-spin text-primary" />
            <p className="mt-4 text-muted-foreground">{t('common.loading')}</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-page">
      <Navbar />
      
      <main className="pt-24 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div 
            className="mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Settings className="w-8 h-8 text-primary" />
                <div>
                  <h1 className="text-3xl font-bold" data-testid="admin-title">
                    Admin Dashboard
                  </h1>
                  <p className="text-muted-foreground">Manage donations, equipment, trees, and transparency</p>
                </div>
              </div>
              <Button variant="outline" onClick={fetchAdminData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </motion.div>

          {/* Quick Stats */}
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="bg-gradient-to-br from-primary/10 to-primary/5">
              <CardContent className="p-4">
                <TreeDeciduous className="w-6 h-6 text-primary mb-2" />
                <p className="text-2xl font-bold">{stats?.total_trees || 0}</p>
                <p className="text-xs text-muted-foreground">Total Trees</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-500/10 to-green-500/5">
              <CardContent className="p-4">
                <CheckCircle className="w-6 h-6 text-green-500 mb-2" />
                <p className="text-2xl font-bold">{stats?.adopted_trees || 0}</p>
                <p className="text-xs text-muted-foreground">Adopted</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-500/10 to-blue-500/5">
              <CardContent className="p-4">
                <Users className="w-6 h-6 text-blue-500 mb-2" />
                <p className="text-2xl font-bold">{stats?.total_users || 0}</p>
                <p className="text-xs text-muted-foreground">Users</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-yellow-500/10 to-yellow-500/5">
              <CardContent className="p-4">
                <DollarSign className="w-6 h-6 text-yellow-500 mb-2" />
                <p className="text-2xl font-bold">${(stats?.total_donated || 0).toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Donated</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-500/10 to-amber-500/5">
              <CardContent className="p-4">
                <AlertCircle className="w-6 h-6 text-amber-500 mb-2" />
                <p className="text-2xl font-bold">{stats?.pending_donations || 0}</p>
                <p className="text-xs text-muted-foreground">Pending</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-500/10 to-purple-500/5">
              <CardContent className="p-4">
                <BarChart3 className="w-6 h-6 text-purple-500 mb-2" />
                <p className="text-2xl font-bold">{stats?.active_batches || 0}</p>
                <p className="text-xs text-muted-foreground">Active Batches</p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Main Tabs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full max-w-3xl grid-cols-6 mb-6">
                <TabsTrigger value="overview">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Overview
                </TabsTrigger>
                <TabsTrigger value="donations">
                  <DollarSign className="w-4 h-4 mr-2" />
                  Donations
                </TabsTrigger>
                <TabsTrigger value="equipment">
                  <TreePine className="w-4 h-4 mr-2" />
                  Equipment
                </TabsTrigger>
                <TabsTrigger value="trees">
                  <TreeDeciduous className="w-4 h-4 mr-2" />
                  Trees
                </TabsTrigger>
                <TabsTrigger value="community" data-testid="admin-community-tab">
                  <Camera className="w-4 h-4 mr-2" />
                  Community
                </TabsTrigger>
                <TabsTrigger value="missions" data-testid="admin-missions-tab">
                  <Target className="w-4 h-4 mr-2" />
                  Missions
                </TabsTrigger>
                <TabsTrigger value="expenses">
                  <Eye className="w-4 h-4 mr-2" />
                  Expenses
                </TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview">
                <div className="grid lg:grid-cols-2 gap-6">
                  {/* Donation Categories Summary */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Donation Categories Progress</CardTitle>
                      <CardDescription>Current funding status across all categories</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {Object.values(categories).map(cat => {
                        const progress = (cat.raised / cat.goal) * 100;
                        return (
                          <div key={cat.id} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="text-2xl">{cat.icon}</span>
                                <span className="font-medium">{cat.name}</span>
                              </div>
                              <span className={`font-bold text-${cat.color}-600`}>
                                {progress.toFixed(1)}%
                              </span>
                            </div>
                            <Progress value={progress} className="h-3" />
                            <div className="flex justify-between text-sm text-muted-foreground">
                              <span>${cat.raised.toLocaleString()} raised</span>
                              <span>Goal: ${cat.goal.toLocaleString()}</span>
                            </div>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>

                  {/* Recent Activity */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Recent Planting Batches</CardTitle>
                      <CardDescription>Latest planting activities</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Batch</TableHead>
                            <TableHead>Progress</TableHead>
                            <TableHead>Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {batches.map(batch => (
                            <TableRow key={batch.batch_id}>
                              <TableCell className="font-medium">{batch.name}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Progress 
                                    value={(batch.planted_count / batch.target_count) * 100} 
                                    className="w-20 h-2" 
                                  />
                                  <span className="text-sm text-muted-foreground">
                                    {batch.planted_count}/{batch.target_count}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell className="text-muted-foreground">
                                {new Date(batch.created_at).toLocaleDateString()}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Donations Tab */}
              <TabsContent value="donations">
                <Card>
                  <CardHeader>
                    <CardTitle>Manage Donation Categories</CardTitle>
                    <CardDescription>Update goals and track donations for each category</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      {Object.entries(categories).map(([id, cat]) => (
                        <div key={id} className="border rounded-lg p-4">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <span className="text-3xl">{cat.icon}</span>
                              <div>
                                <h3 className="font-bold text-lg">{cat.name}</h3>
                                <p className="text-sm text-muted-foreground">
                                  Equipment cost: ${getTotalEquipmentCost(id).toLocaleString()}
                                </p>
                              </div>
                            </div>
                            {editingCategory === id ? (
                              <div className="flex gap-2">
                                <Button size="sm" onClick={() => saveCategory(id)}>
                                  <Save className="w-4 h-4 mr-1" /> Save
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setEditingCategory(null)}>
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ) : (
                              <Button size="sm" variant="outline" onClick={() => setEditingCategory(id)}>
                                <Edit className="w-4 h-4 mr-1" /> Edit
                              </Button>
                            )}
                          </div>
                          
                          {editingCategory === id ? (
                            <div className="grid sm:grid-cols-2 gap-4">
                              <div>
                                <Label>Goal Amount ($)</Label>
                                <Input
                                  type="number"
                                  value={cat.goal}
                                  onChange={(e) => handleUpdateCategory(id, 'goal', e.target.value)}
                                />
                              </div>
                              <div>
                                <Label>Raised Amount ($)</Label>
                                <Input
                                  type="number"
                                  value={cat.raised}
                                  onChange={(e) => handleUpdateCategory(id, 'raised', e.target.value)}
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <div className="flex justify-between text-sm">
                                <span>${cat.raised.toLocaleString()} raised</span>
                                <span className={`font-bold text-${cat.color}-600`}>
                                  {((cat.raised / cat.goal) * 100).toFixed(1)}%
                                </span>
                              </div>
                              <Progress value={(cat.raised / cat.goal) * 100} className="h-3" />
                              <p className="text-sm text-muted-foreground">Goal: ${cat.goal.toLocaleString()}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Equipment Tab */}
              <TabsContent value="equipment">
                <div className="space-y-6">
                  {/* Category Selector */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Manage Equipment & Infrastructure</CardTitle>
                      <CardDescription>Add, edit, or remove equipment items for each donation category</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2 mb-6">
                        {Object.entries(categories).map(([id, cat]) => (
                          <Button
                            key={id}
                            variant={selectedCategory === id ? 'default' : 'outline'}
                            onClick={() => setSelectedCategory(id)}
                            className="flex items-center gap-2"
                          >
                            <span>{cat.icon}</span>
                            {cat.name}
                          </Button>
                        ))}
                      </div>

                      {/* Equipment List */}
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Equipment</TableHead>
                            <TableHead>Brand</TableHead>
                            <TableHead>Unit Cost</TableHead>
                            <TableHead>Qty</TableHead>
                            <TableHead>Total</TableHead>
                            <TableHead>Priority</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="w-24">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {equipment[selectedCategory]?.map(item => (
                            <TableRow key={item.id}>
                              <TableCell>
                                {editingEquipment === item.id ? (
                                  <Input
                                    value={item.name}
                                    onChange={(e) => handleUpdateEquipment(item.id, 'name', e.target.value)}
                                    className="w-full"
                                  />
                                ) : (
                                  <span className="font-medium">{item.name}</span>
                                )}
                              </TableCell>
                              <TableCell>
                                {editingEquipment === item.id ? (
                                  <Input
                                    value={item.brand}
                                    onChange={(e) => handleUpdateEquipment(item.id, 'brand', e.target.value)}
                                    className="w-32"
                                  />
                                ) : (
                                  <span className="text-muted-foreground text-sm">{item.brand}</span>
                                )}
                              </TableCell>
                              <TableCell>
                                {editingEquipment === item.id ? (
                                  <Input
                                    type="number"
                                    value={item.cost}
                                    onChange={(e) => handleUpdateEquipment(item.id, 'cost', e.target.value)}
                                    className="w-24"
                                  />
                                ) : (
                                  `$${item.cost.toLocaleString()}`
                                )}
                              </TableCell>
                              <TableCell>
                                {editingEquipment === item.id ? (
                                  <Input
                                    type="number"
                                    value={item.quantity}
                                    onChange={(e) => handleUpdateEquipment(item.id, 'quantity', e.target.value)}
                                    className="w-16"
                                  />
                                ) : (
                                  item.quantity
                                )}
                              </TableCell>
                              <TableCell className="font-medium">
                                ${(item.cost * item.quantity).toLocaleString()}
                              </TableCell>
                              <TableCell>
                                {editingEquipment === item.id ? (
                                  <Select
                                    value={item.priority}
                                    onValueChange={(v) => handleUpdateEquipment(item.id, 'priority', v)}
                                  >
                                    <SelectTrigger className="w-24">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="high">High</SelectItem>
                                      <SelectItem value="medium">Medium</SelectItem>
                                      <SelectItem value="low">Low</SelectItem>
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <Badge variant={item.priority === 'high' ? 'destructive' : item.priority === 'medium' ? 'default' : 'secondary'}>
                                    {item.priority}
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                {editingEquipment === item.id ? (
                                  <Select
                                    value={item.status}
                                    onValueChange={(v) => handleUpdateEquipment(item.id, 'status', v)}
                                  >
                                    <SelectTrigger className="w-28">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="needed">Needed</SelectItem>
                                      <SelectItem value="ordered">Ordered</SelectItem>
                                      <SelectItem value="received">Received</SelectItem>
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <Badge variant={item.status === 'received' ? 'default' : item.status === 'ordered' ? 'secondary' : 'outline'}>
                                    {item.status}
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  {editingEquipment === item.id ? (
                                    <Button size="sm" variant="ghost" onClick={() => setEditingEquipment(null)}>
                                      <Save className="w-4 h-4" />
                                    </Button>
                                  ) : (
                                    <Button size="sm" variant="ghost" onClick={() => setEditingEquipment(item.id)}>
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                  )}
                                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDeleteEquipment(item.id)}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>

                      {/* Total */}
                      <div className="mt-4 p-4 bg-muted rounded-lg flex justify-between items-center">
                        <span className="font-medium">Total Equipment Cost:</span>
                        <span className="text-2xl font-bold">${getTotalEquipmentCost(selectedCategory).toLocaleString()}</span>
                      </div>

                      {/* Add New Equipment */}
                      <div className="mt-6 border-t pt-6">
                        <h4 className="font-medium mb-4">Add New Equipment</h4>
                        <div className="grid sm:grid-cols-6 gap-4">
                          <div className="sm:col-span-2">
                            <Label>Name</Label>
                            <Input
                              value={newEquipment.name}
                              onChange={(e) => setNewEquipment({...newEquipment, name: e.target.value})}
                              placeholder="Equipment name"
                            />
                          </div>
                          <div>
                            <Label>Brand</Label>
                            <Input
                              value={newEquipment.brand}
                              onChange={(e) => setNewEquipment({...newEquipment, brand: e.target.value})}
                              placeholder="Brand"
                            />
                          </div>
                          <div>
                            <Label>Cost ($)</Label>
                            <Input
                              type="number"
                              value={newEquipment.cost}
                              onChange={(e) => setNewEquipment({...newEquipment, cost: parseFloat(e.target.value) || 0})}
                            />
                          </div>
                          <div>
                            <Label>Quantity</Label>
                            <Input
                              type="number"
                              value={newEquipment.quantity}
                              onChange={(e) => setNewEquipment({...newEquipment, quantity: parseInt(e.target.value) || 1})}
                            />
                          </div>
                          <div className="flex items-end">
                            <Button onClick={handleAddEquipment} className="w-full">
                              <Plus className="w-4 h-4 mr-2" /> Add
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Trees Tab */}
              <TabsContent value="trees">
                <div className="grid lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Add New Tree</CardTitle>
                      <CardDescription>Register a new tree location</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={handleCreateTree} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="lat">Latitude</Label>
                            <Input
                              id="lat"
                              type="number"
                              step="0.000001"
                              value={newTree.lat}
                              onChange={(e) => setNewTree({...newTree, lat: parseFloat(e.target.value)})}
                            />
                          </div>
                          <div>
                            <Label htmlFor="lng">Longitude</Label>
                            <Input
                              id="lng"
                              type="number"
                              step="0.000001"
                              value={newTree.lng}
                              onChange={(e) => setNewTree({...newTree, lng: parseFloat(e.target.value)})}
                            />
                          </div>
                        </div>
                        <div>
                          <Label>Species</Label>
                          <Select
                            value={newTree.species}
                            onValueChange={(v) => setNewTree({...newTree, species: v})}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="lenga">Lenga</SelectItem>
                              <SelectItem value="nire">Ñire</SelectItem>
                              <SelectItem value="coihue">Coihue</SelectItem>
                              <SelectItem value="cipres">Ciprés</SelectItem>
                              <SelectItem value="araucaria">Araucaria</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Button type="submit" disabled={submitting}>
                          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                          Create Tree
                        </Button>
                      </form>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Import from GeoJSON</CardTitle>
                      <CardDescription>Bulk import tree locations from GeoJSON</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <form className="space-y-4">
                        <div>
                          <Label>GeoJSON Data</Label>
                          <Textarea
                            value={importJson}
                            onChange={(e) => setImportJson(e.target.value)}
                            placeholder='{"type": "FeatureCollection", "features": [...]}'
                            className="font-mono text-sm h-32"
                          />
                        </div>
                        <Button disabled={!importJson}>
                          <Upload className="w-4 h-4 mr-2" />
                          Import Trees
                        </Button>
                      </form>
                    </CardContent>
                  </Card>

                  <Card className="lg:col-span-2">
                    <CardHeader>
                      <CardTitle>Create Planting Batch</CardTitle>
                      <CardDescription>Organize trees into planting batches for tracking</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={handleCreateBatch} className="grid sm:grid-cols-4 gap-4">
                        <div className="sm:col-span-2">
                          <Label>Batch Name</Label>
                          <Input
                            value={newBatch.name}
                            onChange={(e) => setNewBatch({...newBatch, name: e.target.value})}
                            placeholder="e.g., Spring 2025 Phase 1"
                          />
                        </div>
                        <div>
                          <Label>Target Trees</Label>
                          <Input
                            type="number"
                            value={newBatch.target_count}
                            onChange={(e) => setNewBatch({...newBatch, target_count: parseInt(e.target.value)})}
                          />
                        </div>
                        <div className="flex items-end">
                          <Button type="submit" disabled={submitting} className="w-full">
                            <Plus className="w-4 h-4 mr-2" />
                            Create Batch
                          </Button>
                        </div>
                      </form>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Community Submissions Tab */}
              <TabsContent value="community" data-testid="admin-community-content">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Community Submissions</CardTitle>
                        <CardDescription>Review and manage community-submitted content</CardDescription>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex gap-2 text-sm">
                          <Badge variant="outline" className="bg-amber-500/10 text-amber-600">
                            {submissionCounts.pending} Pending
                          </Badge>
                          <Badge variant="outline" className="bg-green-500/10 text-green-600">
                            {submissionCounts.approved} Approved
                          </Badge>
                          <Badge variant="outline" className="bg-red-500/10 text-red-600">
                            {submissionCounts.rejected} Rejected
                          </Badge>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => fetchCommunitySubmissions(submissionFilter)}>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Refresh
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Filter */}
                    <div className="flex gap-2 mb-6">
                      {['pending', 'approved', 'rejected', 'all'].map((status) => (
                        <Button
                          key={status}
                          variant={submissionFilter === status ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setSubmissionFilter(status)}
                          data-testid={`filter-${status}`}
                        >
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </Button>
                      ))}
                    </div>

                    {/* Submissions List */}
                    {loadingSubmissions ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-primary" />
                      </div>
                    ) : communitySubmissions.length === 0 ? (
                      <div className="text-center py-12">
                        <Camera className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                        <p className="text-muted-foreground">No submissions found</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {communitySubmissions.map((submission) => (
                          <div
                            key={submission.submission_id}
                            className="border rounded-lg p-4 flex flex-col sm:flex-row gap-4"
                            data-testid={`submission-${submission.submission_id}`}
                          >
                            {/* Preview */}
                            <div className="w-full sm:w-32 h-32 bg-muted rounded-lg overflow-hidden flex-shrink-0">
                              {submission.content_type === 'photo' && submission.file_url && (
                                <img
                                  src={`${API.replace('/api', '')}${submission.file_url}`}
                                  alt={submission.description}
                                  className="w-full h-full object-cover"
                                />
                              )}
                              {submission.content_type === 'video' && (
                                <div className="w-full h-full flex items-center justify-center bg-slate-900">
                                  <Video className="w-8 h-8 text-white" />
                                </div>
                              )}
                              {submission.content_type === 'story' && (
                                <div className="w-full h-full flex items-center justify-center p-2">
                                  <FileText className="w-8 h-8 text-muted-foreground" />
                                </div>
                              )}
                            </div>

                            {/* Details */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2 mb-2">
                                <div>
                                  <h4 className="font-medium text-foreground">{submission.name}</h4>
                                  <p className="text-sm text-muted-foreground">{submission.email}</p>
                                </div>
                                <Badge
                                  variant="outline"
                                  className={
                                    submission.status === 'pending'
                                      ? 'bg-amber-500/10 text-amber-600'
                                      : submission.status === 'approved'
                                      ? 'bg-green-500/10 text-green-600'
                                      : 'bg-red-500/10 text-red-600'
                                  }
                                >
                                  {submission.status}
                                </Badge>
                              </div>

                              <div className="flex flex-wrap gap-3 text-sm text-muted-foreground mb-2">
                                <span className="flex items-center gap-1">
                                  {submission.content_type === 'photo' && <Camera className="w-3.5 h-3.5" />}
                                  {submission.content_type === 'video' && <Video className="w-3.5 h-3.5" />}
                                  {submission.content_type === 'story' && <FileText className="w-3.5 h-3.5" />}
                                  {submission.content_type}
                                </span>
                                <span>{submission.category?.replace('_', ' ')}</span>
                                {submission.event_date && <span>{submission.event_date}</span>}
                                {submission.location && <span>{submission.location}</span>}
                              </div>

                              {submission.description && (
                                <p className="text-sm text-muted-foreground line-clamp-2">
                                  {submission.description}
                                </p>
                              )}

                              {submission.story_text && (
                                <p className="text-sm text-foreground line-clamp-2 italic mt-1">
                                  &ldquo;{submission.story_text}&rdquo;
                                </p>
                              )}

                              {/* Actions */}
                              {submission.status === 'pending' && (
                                <div className="flex gap-2 mt-3">
                                  <Button
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700"
                                    onClick={() => handleReviewSubmission(submission.submission_id, 'approve')}
                                    data-testid={`approve-${submission.submission_id}`}
                                  >
                                    <Check className="w-4 h-4 mr-1" />
                                    Approve
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => handleReviewSubmission(submission.submission_id, 'reject')}
                                    data-testid={`reject-${submission.submission_id}`}
                                  >
                                    <XCircle className="w-4 h-4 mr-1" />
                                    Reject
                                  </Button>
                                </div>
                              )}

                              {submission.status !== 'pending' && (
                                <div className="flex gap-2 mt-3">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-destructive"
                                    onClick={() => handleDeleteSubmission(submission.submission_id)}
                                    data-testid={`delete-${submission.submission_id}`}
                                  >
                                    <Trash2 className="w-4 h-4 mr-1" />
                                    Delete
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Expenses Tab */}
              <TabsContent value="expenses">
                <div className="grid lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Record Expense</CardTitle>
                      <CardDescription>Log expenses for transparency tracking</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={handleCreateExpense} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Category</Label>
                            <Select
                              value={newExpense.category}
                              onValueChange={(v) => setNewExpense({...newExpense, category: v})}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="depining">De-Pining Machinery</SelectItem>
                                <SelectItem value="firefighting">Firefighting Equipment</SelectItem>
                                <SelectItem value="reforestation">Reforestation</SelectItem>
                                <SelectItem value="seedlings">Seedlings</SelectItem>
                                <SelectItem value="labor">Labor</SelectItem>
                                <SelectItem value="transport">Transport</SelectItem>
                                <SelectItem value="admin">Administration</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Amount (USD)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={newExpense.amount}
                              onChange={(e) => setNewExpense({...newExpense, amount: parseFloat(e.target.value)})}
                            />
                          </div>
                        </div>
                        <div>
                          <Label>Description</Label>
                          <Textarea
                            value={newExpense.description}
                            onChange={(e) => setNewExpense({...newExpense, description: e.target.value})}
                            placeholder="What was this expense for?"
                          />
                        </div>
                        <div>
                          <Label>Receipt URL (optional)</Label>
                          <Input placeholder="https://..." />
                        </div>
                        <Button type="submit" disabled={submitting || !newExpense.amount || !newExpense.description}>
                          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                          Record Expense
                        </Button>
                      </form>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Recent Expenses</CardTitle>
                      <CardDescription>Latest recorded expenses</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                          <div>
                            <p className="font-medium">Lenga seedlings (500 units)</p>
                            <p className="text-sm text-muted-foreground">Reforestation • Jan 18, 2025</p>
                          </div>
                          <span className="font-bold text-red-600">-$7,500</span>
                        </div>
                        <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                          <div>
                            <p className="font-medium">Fire pump deposit</p>
                            <p className="text-sm text-muted-foreground">Firefighting • Jan 15, 2025</p>
                          </div>
                          <span className="font-bold text-red-600">-$2,500</span>
                        </div>
                        <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                          <div>
                            <p className="font-medium">Transport to planting site</p>
                            <p className="text-sm text-muted-foreground">Transport • Jan 12, 2025</p>
                          </div>
                          <span className="font-bold text-red-600">-$450</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Missions Tab - Proof Verification */}
              <TabsContent value="missions">
                <div className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Target className="w-5 h-5 text-amber-500" />
                        Pending Mission Verifications
                      </CardTitle>
                      <CardDescription>
                        Review and verify proof-of-work submissions from workers
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button 
                        onClick={fetchPendingProofs} 
                        disabled={loadingMissions}
                        className="mb-4"
                        data-testid="refresh-missions-btn"
                      >
                        {loadingMissions ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading...</>
                        ) : (
                          <><RefreshCw className="w-4 h-4 mr-2" /> Refresh</>
                        )}
                      </Button>
                      
                      {pendingProofs.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
                          <p>No pending proof submissions</p>
                          <p className="text-sm">All missions are either awaiting workers or already verified</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {pendingProofs.map((proof) => (
                            <Card key={proof.submission_id} className="border-amber-500/30">
                              <CardHeader className="pb-2">
                                <div className="flex items-start justify-between">
                                  <div>
                                    <CardTitle className="text-lg">{proof.zone_name || proof.zone_id}</CardTitle>
                                    <CardDescription>
                                      Worker: {proof.worker_name || proof.worker_id} • 
                                      Submitted: {new Date(proof.submitted_at).toLocaleDateString()}
                                    </CardDescription>
                                  </div>
                                  <Badge variant="outline" className="bg-amber-500/20 text-amber-600 border-amber-500">
                                    Pending Review
                                  </Badge>
                                </div>
                              </CardHeader>
                              <CardContent>
                                {/* Mission Details */}
                                <div className="grid grid-cols-3 gap-4 mb-4 text-sm">
                                  <div className="flex items-center gap-2">
                                    <TreePine className="w-4 h-4 text-green-600" />
                                    <span>{proof.hectares || '?'} hectares</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <DollarSign className="w-4 h-4 text-amber-600" />
                                    <span>${proof.bounty_amount?.toLocaleString() || '?'}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <MapPin className="w-4 h-4 text-blue-600" />
                                    <span>{proof.gps_lat ? `${proof.gps_lat.toFixed(4)}, ${proof.gps_lng.toFixed(4)}` : 'No GPS'}</span>
                                  </div>
                                </div>
                                
                                {/* Before/After Photos */}
                                <div className="grid md:grid-cols-2 gap-4 mb-4">
                                  <div>
                                    <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                                      <Camera className="w-4 h-4" /> Before Photos ({proof.before_media?.length || 0})
                                    </h4>
                                    <div className="flex flex-wrap gap-2">
                                      {(proof.before_media || []).map((url, idx) => (
                                        <a 
                                          key={idx} 
                                          href={`${API.replace('/api', '')}${url}`} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="w-20 h-20 rounded-lg overflow-hidden bg-muted hover:ring-2 ring-primary transition-all"
                                        >
                                          <img 
                                            src={`${API.replace('/api', '')}${url}`} 
                                            alt={`Before ${idx + 1}`}
                                            className="w-full h-full object-cover"
                                          />
                                        </a>
                                      ))}
                                    </div>
                                  </div>
                                  <div>
                                    <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                                      <Camera className="w-4 h-4" /> After Photos ({proof.after_media?.length || 0})
                                    </h4>
                                    <div className="flex flex-wrap gap-2">
                                      {(proof.after_media || []).map((url, idx) => (
                                        <a 
                                          key={idx} 
                                          href={`${API.replace('/api', '')}${url}`} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="w-20 h-20 rounded-lg overflow-hidden bg-muted hover:ring-2 ring-primary transition-all"
                                        >
                                          <img 
                                            src={`${API.replace('/api', '')}${url}`} 
                                            alt={`After ${idx + 1}`}
                                            className="w-full h-full object-cover"
                                          />
                                        </a>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Worker Notes */}
                                {proof.notes && (
                                  <div className="bg-muted/50 rounded-lg p-3 mb-4">
                                    <h4 className="font-medium text-sm mb-1">Worker Notes:</h4>
                                    <p className="text-sm text-muted-foreground">{proof.notes}</p>
                                  </div>
                                )}
                                
                                {/* Action Buttons */}
                                <div className="flex gap-2">
                                  <Button
                                    onClick={() => handleVerifyProof(proof.submission_id, 'approved')}
                                    disabled={verifyingProof === proof.submission_id}
                                    className="flex-1 bg-green-600 hover:bg-green-700"
                                    data-testid={`approve-proof-${proof.submission_id}`}
                                  >
                                    {verifyingProof === proof.submission_id ? (
                                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                      <CheckCircle className="w-4 h-4 mr-2" />
                                    )}
                                    Approve & Release Payment
                                  </Button>
                                  <Button
                                    variant="destructive"
                                    onClick={() => handleVerifyProof(proof.submission_id, 'rejected')}
                                    disabled={verifyingProof === proof.submission_id}
                                    data-testid={`reject-proof-${proof.submission_id}`}
                                  >
                                    <XCircle className="w-4 h-4 mr-2" />
                                    Reject
                                  </Button>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
